<template>
  <ToastProvider :duration="DEFAULT_DURATION" swipe-direction="right">
    <ToastRoot
      v-for="t in toasts"
      :key="t.id"
      :type="t.variant === 'error' ? 'foreground' : 'background'"
      :duration="t.sticky ? STICKY_DURATION : (t.duration ?? DEFAULT_DURATION)"
      :default-open="true"
      :class="[
        'flex items-start gap-10 overflow-hidden rounded border bg-white p-12 shadow-lg',
        'data-[state=closed]:opacity-0 data-[state=open]:opacity-100',
        'data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--reka-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--reka-toast-swipe-move-x)]',
        'transition-opacity duration-150',
        borderClass[t.variant],
      ]"
      @update:open="(open: boolean) => !open && dismiss(t.id)"
    >
      <i :class="['text-20 leading-none', iconClass[t.variant], iconColor[t.variant]]" />
      <div class="min-w-0 flex-1">
        <ToastTitle v-if="t.title" :class="['text-14 mb-2 font-semibold', titleColor[t.variant]]">
          {{ t.title }}
        </ToastTitle>
        <ToastDescription :class="['text-14 break-words', bodyColor[t.variant]]">
          {{ t.message }}
        </ToastDescription>
      </div>
      <ToastClose
        aria-label="Dismiss"
        class="text-14 -mt-4 -mr-4 flex h-24 w-24 cursor-pointer items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800"
      >
        <i class="bi-x-lg" />
      </ToastClose>
    </ToastRoot>
    <ToastViewport
      class="fixed right-15 bottom-15 z-[60] flex w-360 max-w-[calc(100vw-30px)] flex-col-reverse gap-10 outline-none"
    />
  </ToastProvider>
</template>

<script setup lang="ts">
import {
  ToastProvider,
  ToastRoot,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastViewport,
} from 'reka-ui';
import { dismiss, toasts, type ToastVariant } from '@/scripts/toast';

/// Data
/**
 * Default ms before auto-dismiss. Matches reka-ui's `ToastProvider` default; surfaced as a named
 * constant so the per-toast fallback in the `:duration` binding is unambiguous.
 */
const DEFAULT_DURATION = 5000;
/**
 * Used in place of `Infinity` for sticky toasts — `setTimeout` truncates above `2 ** 31 - 1`, so
 * passing `Infinity` would fire the close timer immediately. ~24.85 days is effectively forever.
 */
const STICKY_DURATION = 2_147_483_647;

const borderClass: Record<ToastVariant, string> = {
  info: 'border-blue-200 bg-blue-50',
  success: 'border-green-200 bg-green-50',
  warning: 'border-orange-200 bg-orange-50',
  error: 'border-red-200 bg-red-50',
};

const iconClass: Record<ToastVariant, string> = {
  info: 'bi-info-circle-fill',
  success: 'bi-check-circle-fill',
  warning: 'bi-exclamation-triangle-fill',
  error: 'bi-x-circle-fill',
};

const iconColor: Record<ToastVariant, string> = {
  info: 'text-blue-600',
  success: 'text-green-600',
  warning: 'text-orange-600',
  error: 'text-red-600',
};

const titleColor: Record<ToastVariant, string> = {
  info: 'text-blue-900',
  success: 'text-green-900',
  warning: 'text-orange-900',
  error: 'text-red-900',
};

const bodyColor: Record<ToastVariant, string> = {
  info: 'text-blue-800',
  success: 'text-green-800',
  warning: 'text-orange-800',
  error: 'text-red-800',
};
</script>
